<script setup>
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import {
  stripInlineBase64Images,
  SIGNATURE_POSITIONS,
} from 'dashboard/helper/editorHelper';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import RadioCard from 'dashboard/components-next/radioCard/RadioCard.vue';

const props = defineProps({
  messageSignature: {
    type: String,
    default: '',
  },
  messageSignaturePosition: {
    type: String,
    default: SIGNATURE_POSITIONS.ABOVE,
  },
  userName: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['updateSignature', 'updateSignaturePosition']);

const { t } = useI18n();
const signature = ref(props.messageSignature ?? '');
watch(
  () => props.messageSignature ?? '',
  newValue => {
    signature.value = newValue;
  }
);

const positionOptions = [
  {
    key: SIGNATURE_POSITIONS.BELOW,
    title: t(
      'PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.POSITION.BELOW.TITLE'
    ),
    description: t(
      'PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.POSITION.BELOW.DESCRIPTION'
    ),
  },
  {
    key: SIGNATURE_POSITIONS.ABOVE,
    title: t(
      'PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.POSITION.ABOVE.TITLE'
    ),
    description: t(
      'PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.POSITION.ABOVE.DESCRIPTION'
    ),
  },
];

const updateSignature = () => {
  const { sanitizedContent, hasInlineImages } = stripInlineBase64Images(
    signature.value || ''
  );
  signature.value = sanitizedContent.trim();
  if (hasInlineImages) {
    useAlert(
      t('PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.INLINE_IMAGE_WARNING')
    );
  }
  emit('updateSignature', signature.value);
};

const signatureTemplate = position =>
  position === SIGNATURE_POSITIONS.ABOVE
    ? t(
        'PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.POSITION.ABOVE.TEMPLATE',
        { name: props.userName }
      )
    : t(
        'PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.POSITION.BELOW.TEMPLATE',
        { name: props.userName }
      );

const isAutoTemplate = value =>
  !value ||
  [SIGNATURE_POSITIONS.ABOVE, SIGNATURE_POSITIONS.BELOW].some(
    position => value.trim() === signatureTemplate(position).trim()
  );

const selectPosition = position => {
  if (isAutoTemplate(signature.value)) {
    signature.value = signatureTemplate(position);
  }
  emit('updateSignaturePosition', position);
};
</script>

<template>
  <div class="flex flex-col gap-6">
    <form class="flex flex-col gap-6" @submit.prevent="updateSignature()">
      <WootMessageEditor
        id="message-signature-input"
        v-model="signature"
        class="message-editor h-[10rem] !px-3"
        is-format-mode
        :placeholder="$t('PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE.PLACEHOLDER')"
        channel-type="Context::MessageSignature"
        :enable-suggestions="false"
      />
      <div>
        <NextButton
          type="submit"
          :label="
            $t('PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.BTN_TEXT')
          "
        />
      </div>
    </form>
    <div class="flex flex-col gap-3">
      <div>
        <h4 class="text-sm font-medium text-n-slate-12">
          {{
            $t('PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.POSITION.TITLE')
          }}
        </h4>
        <p class="mb-0 text-sm text-n-slate-11">
          {{
            $t('PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.POSITION.NOTE')
          }}
        </p>
      </div>
      <div
        class="flex flex-col justify-between w-full gap-3 sm:gap-4 sm:flex-row"
      >
        <RadioCard
          v-for="option in positionOptions"
          :id="option.key"
          :key="option.key"
          :label="option.title"
          :description="option.description"
          :is-active="messageSignaturePosition === option.key"
          class="sm:flex-1"
          @select="selectPosition"
        />
      </div>
    </div>
  </div>
</template>
